if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        /* THE QUICK-ADD BUTTON NEVER CAME ALIVE. Tracy 2026-09-25, on the live
           site: "you can't actually click add from the collection page... you can
           select a size, it tells you to select a size, but the button doesn't
           change to add to bag. It's dead."

           Measured, not guessed. The server is fine — the modal fetches
           ?section_id=…&option_values=… and that response carries an ENABLED
           "Add to Bag" (verified against live). The size chip registers too: the
           radio ends up checked. What fails is the last step.

           Dawn enables the button via `this.productForm?.toggleSubmitButton(...)`,
           and `get productForm()` is a plain descendant lookup for <product-form>
           inside <product-info>. In the section response product-form IS inside
           product-info; once injected into the modal it is NOT (confirmed in
           Chrome: product-info at `div > div#QuickAddInfo-… > product-info`,
           product-form at `div > div > product-form`, and pi.contains(pf) false).
           So the optional chain swallows the call — no error, no console warning,
           nothing enables. Silent by construction, which is why it survived.

           Rather than fight Dawn's nesting, take the state from the response we
           already have. variantChange publishes the parsed `html`, so read the
           authoritative button out of it and apply it to whichever submit button
           this modal is actually showing. */
        /* Fire the local resolve on the tap itself — capture phase so it runs
           before Dawn kicks off its section fetch. */
        this.addEventListener('change', (e) => {
          if (e.target && e.target.closest && e.target.closest('variant-selects')) {
            this.localVariantSync();
          }
        }, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data;
            if (!data || !data.html) return;
            /* Only react to the render for the product this modal is showing. */
            const info = this.querySelector('product-info');
            if (!info || data.sectionId !== (info.dataset.originalSection || info.dataset.section)) return;
            this.syncSubmitButton(data.html, data.sectionId);
          });
        }
      }

      /* DECIDE THE BUTTON LOCALLY, THE MOMENT A SIZE IS TAPPED.

         Tracy 2026-09-25: "I selected a size, there's too long of a delay before
         the buy button changes to add to bag — that's on both desktop and mobile."
         Measured on staging: 479 ms.

         All of that is a round trip. Dawn asks the server to re-render the whole
         product section (~106 KB) and only then reads the button out of it. But
         the answer is already on the page: the modal ships the product's variant
         JSON, so which variant a size maps to, and whether it is available, is
         knowable here without asking anyone.

         The hidden id input is updated in the SAME breath as the button. That is
         the part that makes this safe rather than merely fast — enabling the
         button while the form still carried the previous variant would let someone
         add the wrong size, which is far worse than a slow button.

         The server render still lands a few hundred ms later and still runs
         syncSubmitButton(); if it ever disagrees, it wins. This is an optimistic
         head start, not a replacement. */
      localVariantSync() {
        /* Scope every lookup to the MODAL, not to <product-info>. In this modal
           product-form ends up outside product-info (see the note above), so
           anything scoped to product-info silently finds nothing. */
        let variants;
        try {
          const json = this.querySelector('script[data-cnc-variant-map]');
          variants = json && JSON.parse(json.textContent);
        } catch (e) {
          return; /* malformed or absent — the server path still covers it */
        }
        if (!Array.isArray(variants) || !variants.length) return;

        /* What is selected right now, in option order. */
        const chosen = [];
        this.querySelectorAll('fieldset').forEach((set, i) => {
          const checked = set.querySelector('input[type="radio"]:checked');
          const select = set.querySelector('select');
          chosen[i] = checked ? checked.value : select ? select.value : null;
        });
        if (!chosen.length || chosen.every((v) => v === null)) return;

        /* Match on VALUE MEMBERSHIP, not on position.

           Colourways are separate products here, so a PDP usually renders only
           the Size fieldset even though the variant carries ["Navy","S",null].
           Comparing the first rendered fieldset against option1 therefore asked
           "is S equal to Navy" and never matched — which is why the first version
           of this silently did nothing and left the 400ms server wait in place.

           Requiring every chosen value to appear somewhere in the variant's
           options is correct whether one option is rendered or three, because a
           value like a size is unique within a product. */
        const match = variants.find((v) =>
          chosen.every((val) => val === null || (v.o || []).indexOf(val) !== -1)
        );
        if (!match) return;

        /* Point the form at the variant BEFORE the button becomes pressable. */
        this.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = match.id;
        });

        const button = this.querySelector('.product-form__submit, [name="add"]');
        if (!button) return;
        const available = match.a !== false;
        if (available) {
          button.removeAttribute('disabled');
        } else {
          button.setAttribute('disabled', 'disabled');
        }
        button.setAttribute('aria-disabled', available ? 'false' : 'true');
        const dest = button.querySelector('span:not(.svg-wrapper)') || button;
        const label = available
          ? (window.variantStrings && window.variantStrings.addToCart) || 'Add to Bag'
          : (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
        if (dest.textContent.trim() !== label) dest.textContent = label;
        button.classList.toggle('cnc-bag-ready', available);
      }

      /* Copy the server's verdict onto the button the shopper can actually see. */
      syncSubmitButton(html, sectionId) {
        const source =
          html.getElementById(`ProductSubmitButton-${sectionId}`) ||
          html.querySelector('.product-form__submit, [name="add"]');
        const button = this.querySelector('.product-form__submit, [name="add"]');
        if (!source || !button) return;

        const disabled = source.hasAttribute('disabled');
        if (disabled) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        /* The label is server-rendered here ("Select your size" -> "Add to Bag"),
           so take it from the source too rather than assuming Dawn's strings. */
        const sourceText = source.querySelector('span:not(.svg-wrapper)') || source;
        const destText = button.querySelector('span:not(.svg-wrapper)') || button;
        const label = (sourceText.textContent || '').trim();
        if (label && destText.textContent.trim() !== label) destText.textContent = label;

        /* Keep the CNC "ready" styling honest — the CSS keys off this class. */
        button.classList.toggle('cnc-bag-ready', !disabled);
      }

      /* Dawn fetched the WHOLE product page for a quick-add — measured on this store at
         1,170 KB and ~912 ms, of which the modal uses one section. The Section Rendering
         API returns just that section at ~59 KB.

         The section id is Shopify-generated (template--<id>__main), so it is NOT written
         down anywhere here: the first open reads it off the page it already fetched
         (product-info[data-section]) and caches it for the session. If the cached id ever
         stops resolving — a rebuilt template, a different product template — the fast path
         fails soft and the full page is fetched again, which re-derives the new id.

         Rendering the section by FILE NAME (?section_id=main-product) is smaller still at
         37 KB but renders with the section's DEFAULT settings: no title, no variant picker
         and no add-to-cart button. Measured, rejected. */
      static SECTION_KEY = 'cnc:quickAddSectionId';

      static readCachedSectionId() {
        try {
          return sessionStorage.getItem(QuickAddModal.SECTION_KEY);
        } catch (e) {
          return null;
        }
      }

      static cacheSectionId(id) {
        if (!id) return;
        try {
          sessionStorage.setItem(QuickAddModal.SECTION_KEY, id);
        } catch (e) {
          /* private mode — the slow path still works */
        }
      }

      static parseProductInfo(text) {
        return new DOMParser().parseFromString(text, 'text/html').querySelector('product-info');
      }

      /* Response cache for the section markup, keyed by product URL.

         Measured 2026-09-22 (emulated iPhone, 4G, 4x CPU): 1,288 ms to open the
         first drawer and ~700 ms for every one after it. The section id was already
         being cached, so the remaining cost is simply that the fetch does not START
         until the tap. Discovering the id once made the payload small; it did nothing
         about the round trip.

         So this caches the response TEXT, not the parsed node: preprocessHTML mutates
         the element it is handed (it rewrites every id to avoid duplicates), so a
         cached node would come back corrupted on the second open. Parsing is cheap;
         the network is not.

         TTL is deliberately short. This markup carries price and availability, and a
         drawer that adds a sold-out variant to the bag is worse than a slow one. */
      static TTL_MS = 60000;
      static MAX_ENTRIES = 8;
      static cache = new Map();

      static cacheGet(url) {
        const hit = QuickAddModal.cache.get(url);
        if (!hit) return null;
        if (Date.now() - hit.t > QuickAddModal.TTL_MS) {
          QuickAddModal.cache.delete(url);
          return null;
        }
        return hit.promise;
      }

      static cacheSet(url, promise) {
        if (QuickAddModal.cache.size >= QuickAddModal.MAX_ENTRIES) {
          QuickAddModal.cache.delete(QuickAddModal.cache.keys().next().value);
        }
        QuickAddModal.cache.set(url, { t: Date.now(), promise });
      }

      /* Fetch the section markup as TEXT. Resolves null if the fast path is not
         usable yet, so callers fall back to the full page and re-derive the id. */
      static fetchSectionText(url) {
        const sectionId = QuickAddModal.readCachedSectionId();
        if (!sectionId) return Promise.resolve(null);
        const sep = url.includes('?') ? '&' : '?';
        return fetch(`${url}${sep}section_id=${encodeURIComponent(sectionId)}`)
          .then((res) => (res.ok ? res.text() : null))
          .catch(() => null);
      }

      static async loadProductInfo(url) {
        /* An in-flight or completed prefetch from warm() lands here — this is the
           whole point: by the time the tap is processed the bytes are already on
           their way, so the drawer opens on parse time rather than round-trip time. */
        const pending = QuickAddModal.cacheGet(url);
        if (pending) {
          try {
            const text = await pending;
            if (text) {
              const el = QuickAddModal.parseProductInfo(text);
              if (el && el.querySelector('button[name="add"]')) return el;
            }
          } catch (e) {
            /* fall through to a live fetch */
          }
          QuickAddModal.cache.delete(url);
        }

        const text = await QuickAddModal.fetchSectionText(url);
        if (text) {
          const el = QuickAddModal.parseProductInfo(text);
          if (el && el.querySelector('button[name="add"]')) return el;
        }

        /* Slow path: no id yet, or the cached id stopped resolving (rebuilt template,
           different product template). Fetching the full page re-derives it. */
        const res = await fetch(url);
        const el = QuickAddModal.parseProductInfo(await res.text());
        if (el) QuickAddModal.cacheSectionId(el.dataset.section);
        return el;
      }

      /* Warm on INTENT — pointerenter / touchstart on the button itself, so the cost
         lands on someone reaching for a cart rather than on every collection visit.

         Two jobs, and it used to do only the first: discover the section id once, AND
         prefetch THIS product's markup. Returning early as soon as an id was cached
         meant every open after the first still paid the full round trip on tap. */
      static warm(url) {
        if (!url) return;

        if (!QuickAddModal.readCachedSectionId()) {
          if (QuickAddModal.warming) return;
          QuickAddModal.warming = true;
          /* Deriving the id also primes the cache for this url on the way through. */
          QuickAddModal.loadProductInfo(url)
            .then(() => QuickAddModal.warm(url))
            .catch(() => {})
            .finally(() => {
              QuickAddModal.warming = false;
            });
          return;
        }

        if (QuickAddModal.cacheGet(url)) return;
        QuickAddModal.cacheSet(url, QuickAddModal.fetchSectionText(url));
      }

      /* Tracy 2026-09-22: "They should NOT scroll to add to bag."

         The button used to live inside the scrolling body as position:sticky;bottom:0.
         Sticky only pins while its containing block is in the scrollport, and the
         drawer's body is clipped by a panel with overflow:hidden — so on a real
         iPhone the button ended up below the cut with nothing to pin against, and
         the drawer appeared to stop after the Size label. It never reproduced in
         emulation, which is exactly why it should not depend on sticky at all.

         So the form is MOVED out of the scrolling body into a dock that is a
         sibling of it inside the panel. The panel is a flex column, the body flexes
         and scrolls, the dock does not — the button is then structurally incapable
         of scrolling out of view, at any viewport, whatever dvh does. */
      dockAddToBag() {
        const panel = this.querySelector('.quick-add-modal__content');
        const form = this.modalContent.querySelector('.product-form');
        if (!panel || !form) return;

        let dock = panel.querySelector('.cnc-quickadd-dock');
        if (!dock) {
          dock = document.createElement('div');
          dock.className = 'cnc-quickadd-dock';
          panel.appendChild(dock);
        }
        dock.replaceChildren(form);
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        /* The dock lives outside modalContent, so clearing modalContent alone would
           leave the previous product's button behind the next one. */
        const dock = this.querySelector('.cnc-quickadd-dock');
        if (dock) dock.replaceChildren();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        QuickAddModal.loadProductInfo(opener.getAttribute('data-product-url'))
          .then((productElement) => {
            if (!productElement) return;

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.dockAddToBag();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
